/**
 * Voice AI — Agent Realism Tab
 * Configure human-like behaviors: interactions, interruption effects,
 * emotion, background noise, and coworker consultations.
 * Audio files are shared across all agents — each agent selects which to use.
 * Dispatches: save, agent-changed
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { confirmService } from '../../services/confirm-service.js';
import { mediaService } from '../../services/media-service.js';
import { mediaLibraryService } from '../../services/media-library-service.js';

const DEFAULT_INTERACTION_TYPES = [
  { key: 'laughing', label: 'Laughing', audioIds: [] },
  { key: 'coughing', label: 'Coughing', audioIds: [] },
  { key: 'typing', label: 'Typing Sounds', audioIds: [] },
  { key: 'thinking', label: 'Thinking / Hmm', audioIds: [] },
  { key: 'affirmation', label: 'Mm-hmm / Uh-huh', audioIds: [] },
];

const DEFAULT_PHRASES = [
  { text: "Oh I'm sorry, what was that?", audioId: null },
  { text: "Go ahead", audioId: null },
  { text: "Oh, pardon me — what were you saying?", audioId: null },
];

const DEFAULT_REALISM = {
  interactions: { enabled: false, types: DEFAULT_INTERACTION_TYPES },
  interruption_effects: { enabled: false, phrases: DEFAULT_PHRASES },
  emotion: { enabled: false, intensity: 0.5 },
  background_noise: { enabled: false, volume: 0.3, audioIds: [] },
  ask_coworker: { enabled: false, coworker_name: 'Christy', frequency: 0.2 },
};

export class AgentRealismTab extends LitElement {
  static properties = {
    agent: { type: Object },
    saving: { type: Boolean },
    _expanded: { type: Object, state: true },
    _audioFiles: { type: Array, state: true },
    _playingId: { type: Number, state: true },
    _expandedTypes: { type: Object, state: true },
    _addingType: { type: Boolean, state: true },
    _newTypeLabel: { type: String, state: true },
    _addingPhrase: { type: Boolean, state: true },
    _newPhraseText: { type: String, state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles, css`
    .realism-section {
      background: var(--glass-bg);
      border: 1px solid var(--glass-border);
      border-radius: var(--radius-lg);
      margin-bottom: var(--space-3);
      overflow: hidden;
    }
    .section-toggle {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-4);
      cursor: pointer;
      user-select: none;
      font-weight: var(--font-semibold);
      font-size: var(--text-sm);
    }
    .section-toggle:hover { background: var(--color-bg-hover); }
    .section-toggle .section-icon {
      width: 32px; height: 32px; border-radius: var(--radius-md);
      display: flex; align-items: center; justify-content: center;
      font-size: 0.9rem;
    }
    .section-toggle .section-title { flex: 1; }
    .chevron {
      transition: transform var(--duration-fast) var(--ease-out);
      color: var(--color-text-tertiary);
      font-size: 0.75rem;
    }
    .chevron.open { transform: rotate(90deg); }
    .section-body {
      padding: 0 var(--space-4) var(--space-4);
      border-top: 1px solid var(--color-border-subtle);
    }
    .section-desc {
      font-size: var(--text-xs);
      color: var(--color-text-tertiary);
      margin-bottom: var(--space-3);
      padding-top: var(--space-3);
    }

    /* Interaction rows */
    .interaction-row {
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-md);
      margin-bottom: var(--space-2);
      overflow: hidden;
    }
    .interaction-header {
      display: flex; align-items: center; gap: var(--space-2);
      padding: var(--space-2) var(--space-3);
      cursor: pointer; font-size: var(--text-sm);
    }
    .interaction-header:hover { background: var(--color-bg-hover); }
    .interaction-header .type-label { flex: 1; font-weight: var(--font-medium); }
    .interaction-header .file-count {
      font-size: var(--text-xs); color: var(--color-text-tertiary);
      background: var(--color-bg-tertiary); padding: 2px 8px;
      border-radius: var(--radius-full);
    }
    .interaction-body {
      padding: var(--space-2) var(--space-3) var(--space-3);
      border-top: 1px solid var(--color-border-subtle);
      background: var(--color-bg-secondary);
    }

    /* Audio file card */
    .audio-card {
      display: flex; align-items: center; gap: var(--space-2);
      padding: var(--space-2) var(--space-3);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-md);
      margin-bottom: var(--space-2);
      font-size: var(--text-xs);
      transition: all var(--duration-fast);
    }
    .audio-card:hover { background: var(--color-bg-hover); }
    .audio-card .file-name { flex: 1; color: var(--color-text-secondary); }
    .audio-card .file-size { color: var(--color-text-tertiary); }

    /* Audio file row (non-selectable, for delete/play) */
    .audio-file {
      display: flex; align-items: center; gap: var(--space-2);
      padding: var(--space-2) 0;
      border-bottom: 1px solid var(--color-border-subtle);
      font-size: var(--text-xs);
    }
    .audio-file:last-child { border-bottom: none; }
    .audio-file .file-name { flex: 1; color: var(--color-text-secondary); }
    .audio-file .file-size { color: var(--color-text-tertiary); margin-right: var(--space-2); }

    /* Phrase cards */
    .phrase-card {
      display: flex; align-items: center; gap: var(--space-2);
      padding: var(--space-2) var(--space-3);
      border: 1px solid var(--color-border-subtle);
      border-radius: var(--radius-md);
      margin-bottom: var(--space-2);
      background: var(--color-bg-secondary);
    }
    .phrase-card .form-input { flex: 1; font-size: var(--text-xs); min-height: 32px; }

    /* Info note */
    .info-note {
      display: flex; align-items: flex-start; gap: var(--space-2);
      padding: var(--space-3);
      background: rgba(59, 130, 246, 0.06);
      border: 1px solid rgba(59, 130, 246, 0.15);
      border-radius: var(--radius-md);
      font-size: var(--text-xs);
      color: var(--color-text-secondary);
      margin-top: var(--space-3);
    }
    .info-note i { color: var(--color-info); margin-top: 1px; }

    /* Slider row */
    .slider-row {
      display: flex; align-items: center; gap: var(--space-3);
      margin-top: var(--space-2);
    }
    .slider-row input[type="range"] { flex: 1; }
    .slider-row .slider-label {
      font-size: var(--text-xs); color: var(--color-text-tertiary); min-width: 50px;
    }
    .slider-row .slider-value {
      font-size: var(--text-sm); font-weight: var(--font-medium);
      min-width: 40px; text-align: center;
    }

    /* Section label */
    .section-label {
      font-size: var(--text-xs);
      font-weight: var(--font-semibold);
      color: var(--color-text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-top: var(--space-3);
      margin-bottom: var(--space-2);
    }

    /* Actions footer */
    .actions-footer {
      display: flex; justify-content: flex-end; gap: var(--space-2);
      padding: var(--space-4) 0 0;
      border-top: 1px solid var(--color-border-subtle);
      margin-top: var(--space-3);
    }
  `];

  constructor() {
    super();
    this._expanded = new Set(['interactions']);
    this._audioFiles = [];
    this._playingId = null;
    this._expandedTypes = new Set();
    this._addingType = false;
    this._newTypeLabel = '';
    this._addingPhrase = false;
    this._newPhraseText = '';
    this._audioEl = null;
    this._lastAgentId = null;
  }

  updated(changed) {
    if (changed.has('agent') && this.agent?.id !== this._lastAgentId) {
      this._lastAgentId = this.agent?.id;
      this._loadAudioFiles();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._audioEl) { this._audioEl.pause(); this._audioEl = null; }
  }

  /* ─── Data Helpers ─── */

  _getRealism() {
    const r = this.agent?.voiceSettings?.realism;
    if (!r) return JSON.parse(JSON.stringify(DEFAULT_REALISM));
    return {
      interactions: r.interactions || DEFAULT_REALISM.interactions,
      interruption_effects: r.interruption_effects || DEFAULT_REALISM.interruption_effects,
      emotion: r.emotion || DEFAULT_REALISM.emotion,
      background_noise: r.background_noise || DEFAULT_REALISM.background_noise,
      ask_coworker: r.ask_coworker || DEFAULT_REALISM.ask_coworker,
    };
  }

  _updateRealism(updater) {
    const agent = { ...this.agent };
    const vs = { ...(agent.voiceSettings || {}) };
    const realism = JSON.parse(JSON.stringify(this._getRealism()));
    updater(realism);
    vs.realism = realism;
    agent.voiceSettings = vs;
    // Update locally so _save() reads the new value immediately
    this.agent = agent;
    this.dispatchEvent(new CustomEvent('agent-changed', { detail: { agent } }));
  }

  _save() {
    this.dispatchEvent(new CustomEvent('save', { detail: { agent: this.agent } }));
  }

  /* ─── Audio File API (shared library via mediaService) ─── */

  async _loadAudioFiles() {
    try {
      const data = await mediaService.loadFiles({ fileType: 'audio' });
      if (data.success) this._audioFiles = data.files || [];
    } catch (err) {
      console.error('[RealismTab] Load audio failed:', err);
    }
  }

  _getFilesFor(category, itemType) {
    const realism = this._getRealism();
    let ids = [];
    if (category === 'interaction' && itemType) {
      ids = realism.interactions.types.find(t => t.key === itemType)?.audioIds || [];
    } else if (category === 'background_noise') {
      ids = realism.background_noise.audioIds || [];
    }
    return ids.map(id => this._audioFiles.find(f => f.id === id)).filter(Boolean);
  }

  /* ─── Media Library Integration ─── */

  async _openLibrary(category, itemType) {
    const realism = this._getRealism();
    let selectedIds = [];
    if (category === 'interaction' && itemType) {
      selectedIds = realism.interactions.types.find(t => t.key === itemType)?.audioIds || [];
    } else if (category === 'background_noise') {
      selectedIds = realism.background_noise.audioIds || [];
    }

    const result = await mediaLibraryService.show({
      fileType: 'audio',
      tags: `realism,${category}${itemType ? ',' + itemType : ''}`,
      uploadedBy: 'realism',
      multiple: true,
      selectedIds,
      accept: 'audio/*',
    });
    if (!result) return;

    const newIds = result.files.map(f => f.id);
    this._updateRealism(r => {
      if (category === 'interaction' && itemType) {
        const type = r.interactions.types.find(t => t.key === itemType);
        if (type) type.audioIds = newIds;
      } else if (category === 'background_noise') {
        r.background_noise.audioIds = newIds;
      }
    });
    await this._loadAudioFiles();
    this._save();
  }

  async _openLibraryForPhrase(index) {
    const phrase = this._getRealism().interruption_effects.phrases[index];
    const result = await mediaLibraryService.show({
      fileType: 'audio',
      tags: 'realism,interruption',
      uploadedBy: 'realism',
      multiple: false,
      selectedIds: phrase?.audioId ? [phrase.audioId] : [],
      accept: 'audio/*',
    });
    if (!result) return;

    this._updateRealism(r => {
      r.interruption_effects.phrases[index].audioId = result.files[0]?.id || null;
    });
    await this._loadAudioFiles();
    this._save();
  }

  _unlinkAudioFile(audioId, category, itemType) {
    if (this._playingId === audioId) { this._audioEl?.pause(); this._playingId = null; }
    this._updateRealism(r => {
      if (category === 'interaction' && itemType) {
        const type = r.interactions.types.find(t => t.key === itemType);
        if (type) type.audioIds = type.audioIds.filter(id => id !== audioId);
      } else if (category === 'background_noise') {
        r.background_noise.audioIds = r.background_noise.audioIds.filter(id => id !== audioId);
      }
    });
    this._save();
  }

  /* ─── Audio Playback ─── */

  _togglePlay(file, volume) {
    if (this._playingId === file.id) {
      this._audioEl?.pause(); this._playingId = null; return;
    }
    if (this._audioEl) this._audioEl.pause();
    this._audioEl = new Audio(file.url);
    this._audioEl.volume = volume != null ? volume : 1.0;
    this._audioEl.onended = () => { this._playingId = null; };
    this._audioEl.play();
    this._playingId = file.id;
  }

  _toast(message) {
    this.dispatchEvent(new CustomEvent('toast', { detail: { message }, bubbles: true, composed: true }));
  }

  _toggleSection(key) {
    const s = new Set(this._expanded);
    s.has(key) ? s.delete(key) : s.add(key);
    this._expanded = s;
  }

  _toggleType(key) {
    const s = new Set(this._expandedTypes);
    s.has(key) ? s.delete(key) : s.add(key);
    this._expandedTypes = s;
  }

  _formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  /* ─── Render ─── */

  render() {
    const realism = this._getRealism();
    return html`
      ${iconStyles}

      ${this._renderInteractions(realism)}
      ${this._renderInterruptionEffects(realism)}
      ${this._renderEmotion(realism)}
      ${this._renderBackgroundNoise(realism)}
      ${this._renderAskCoworker(realism)}

      <div class="actions-footer">
        <button class="btn btn-primary" @click=${this._save} ?disabled=${this.saving}>
          ${this.saving ? 'Saving...' : 'Save Realism Settings'}
        </button>
      </div>
    `;
  }

  /* ─── Section 1: Interactions ─── */

  _renderInteractions(realism) {
    const isOpen = this._expanded.has('interactions');
    const cfg = realism.interactions;
    return html`
      <div class="realism-section">
        <div class="section-toggle" @click=${() => this._toggleSection('interactions')}>
          <div class="section-icon" style="background:rgba(251,191,36,0.12);color:#f59e0b;">
            <i class="fa-sharp fa-light fa-face-laugh-beam"></i>
          </div>
          <span class="section-title">Interactions</span>
          <div class="toggle ${cfg.enabled ? 'active' : ''}"
            @click=${(e) => { e.stopPropagation(); this._updateRealism(r => { r.interactions.enabled = !r.interactions.enabled; }); }}
          ><div class="toggle-slider"></div></div>
          <i class="fa-sharp fa-light fa-chevron-right chevron ${isOpen ? 'open' : ''}"></i>
        </div>
        ${isOpen ? html`
          <div class="section-body">
            <div class="section-desc">
              Natural sound effects the AI can use during conversation. Select audio files per type — multiple files enable random variety.
            </div>
            ${(cfg.types || []).map(type => this._renderInteractionType(type))}

            ${this._addingType ? html`
              <div style="display:flex;gap:var(--space-2);margin-top:var(--space-2);">
                <input class="form-input" type="text" placeholder="e.g. Sipping Coffee"
                  .value=${this._newTypeLabel}
                  @input=${e => { this._newTypeLabel = e.target.value; }}
                  @keydown=${e => { if (e.key === 'Enter') this._confirmAddType(); }}
                  style="flex:1;min-height:32px;font-size:var(--text-xs);"
                />
                <button class="btn btn-primary btn-sm" @click=${this._confirmAddType} ?disabled=${!this._newTypeLabel?.trim()}>Add</button>
                <button class="btn btn-secondary btn-sm" @click=${() => { this._addingType = false; }}>Cancel</button>
              </div>
            ` : html`
              <button class="btn btn-ghost btn-sm" style="margin-top:var(--space-2);"
                @click=${() => { this._addingType = true; this._newTypeLabel = ''; }}
              >
                <i class="fa-sharp fa-light fa-plus" style="margin-right:var(--space-1);"></i> Add Custom Type
              </button>
            `}
          </div>
        ` : ''}
      </div>
    `;
  }

  _renderInteractionType(type) {
    const isOpen = this._expandedTypes.has(type.key);
    const allFiles = this._getFilesFor('interaction', type.key);

    return html`
      <div class="interaction-row">
        <div class="interaction-header" @click=${() => this._toggleType(type.key)}>
          <i class="fa-sharp fa-light fa-chevron-right chevron ${isOpen ? 'open' : ''}" style="font-size:0.65rem;"></i>
          <span class="type-label">${type.label}</span>
          <span class="file-count">${allFiles.length} file${allFiles.length !== 1 ? 's' : ''}</span>
          <button class="btn btn-ghost btn-sm" style="padding:2px 6px;min-height:24px;"
            @click=${(e) => { e.stopPropagation(); this._removeInteractionType(type.key); }}
            title="Remove type"
          ><i class="fa-sharp fa-light fa-xmark" style="font-size:0.7rem;color:var(--color-text-tertiary);"></i></button>
        </div>
        ${isOpen ? html`
          <div class="interaction-body">
            ${allFiles.length > 0 ? html`
              ${allFiles.map(f => this._renderSelectableAudio(f, 'interaction', type.key))}
            ` : html`
              <div style="font-size:var(--text-xs);color:var(--color-text-tertiary);padding:var(--space-2) 0;">No audio files for this type yet</div>
            `}
            <button class="btn btn-secondary btn-sm" style="margin-top:var(--space-2);width:100%;"
              @click=${() => this._openLibrary('interaction', type.key)}
            >
              <i class="fa-sharp fa-light fa-folder-open" style="margin-right:var(--space-1);"></i> Choose from Library
            </button>
          </div>
        ` : ''}
      </div>
    `;
  }

  _confirmAddType() {
    const label = this._newTypeLabel?.trim();
    if (!label) return;
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    this._updateRealism(r => {
      if (!r.interactions.types.find(t => t.key === key)) {
        r.interactions.types.push({ key, label, audioIds: [] });
      }
    });
    this._addingType = false;
    this._newTypeLabel = '';
  }

  async _removeInteractionType(key) {
    const confirmed = await confirmService.show({
      title: 'Remove Interaction Type',
      description: 'This will remove this type from the agent. Audio files are not deleted.',
    });
    if (!confirmed) return;
    this._updateRealism(r => {
      r.interactions.types = r.interactions.types.filter(t => t.key !== key);
    });
  }

  /* ─── Section 2: Interruption Effects ─── */

  _renderInterruptionEffects(realism) {
    const isOpen = this._expanded.has('interruption');
    const cfg = realism.interruption_effects;
    return html`
      <div class="realism-section">
        <div class="section-toggle" @click=${() => this._toggleSection('interruption')}>
          <div class="section-icon" style="background:rgba(239,68,68,0.1);color:#ef4444;">
            <i class="fa-sharp fa-light fa-hand"></i>
          </div>
          <span class="section-title">Interruption Effects</span>
          <div class="toggle ${cfg.enabled ? 'active' : ''}"
            @click=${(e) => { e.stopPropagation(); this._updateRealism(r => { r.interruption_effects.enabled = !r.interruption_effects.enabled; }); }}
          ><div class="toggle-slider"></div></div>
          <i class="fa-sharp fa-light fa-chevron-right chevron ${isOpen ? 'open' : ''}"></i>
        </div>
        ${isOpen ? html`
          <div class="section-body">
            <div class="section-desc">
              Natural recovery phrases when the caller interrupts. The AI will randomly pick one. Optionally upload audio to play instead of TTS.
            </div>
            ${(cfg.phrases || []).map((phrase, i) => this._renderPhrase(phrase, i))}

            ${this._addingPhrase ? html`
              <div style="display:flex;gap:var(--space-2);margin-top:var(--space-2);">
                <input class="form-input" type="text" placeholder='e.g. "Oh sorry, go ahead"'
                  .value=${this._newPhraseText}
                  @input=${e => { this._newPhraseText = e.target.value; }}
                  @keydown=${e => { if (e.key === 'Enter') this._confirmAddPhrase(); }}
                  style="flex:1;min-height:32px;font-size:var(--text-xs);"
                />
                <button class="btn btn-primary btn-sm" @click=${this._confirmAddPhrase} ?disabled=${!this._newPhraseText?.trim()}>Add</button>
                <button class="btn btn-secondary btn-sm" @click=${() => { this._addingPhrase = false; }}>Cancel</button>
              </div>
            ` : html`
              <button class="btn btn-ghost btn-sm" style="margin-top:var(--space-2);"
                @click=${() => { this._addingPhrase = true; this._newPhraseText = ''; }}
              >
                <i class="fa-sharp fa-light fa-plus" style="margin-right:var(--space-1);"></i> Add Phrase
              </button>
            `}
          </div>
        ` : ''}
      </div>
    `;
  }

  _renderPhrase(phrase, index) {
    const audioFile = phrase.audioId ? this._audioFiles.find(f => f.id === phrase.audioId) : null;
    return html`
      <div class="phrase-card">
        <input class="form-input" type="text"
          .value=${phrase.text}
          @change=${(e) => {
            this._updateRealism(r => { r.interruption_effects.phrases[index].text = e.target.value; });
          }}
        />
        ${audioFile ? html`
          <button class="btn btn-ghost btn-sm" style="min-height:28px;padding:2px 6px;"
            @click=${() => this._togglePlay(audioFile)}
            title="Play audio"
          >
            <i class="fa-sharp fa-light ${this._playingId === audioFile.id ? 'fa-stop' : 'fa-play'}" style="font-size:0.7rem;"></i>
          </button>
          <button class="btn btn-ghost btn-sm" style="min-height:28px;padding:2px 6px;"
            @click=${() => this._removePhraseAudio(index)}
            title="Remove audio"
          >
            <i class="fa-sharp fa-light fa-xmark" style="font-size:0.7rem;color:var(--color-error);"></i>
          </button>
        ` : html`
          <button class="btn btn-ghost btn-sm" style="min-height:28px;padding:2px 6px;"
            @click=${() => this._openLibraryForPhrase(index)}
            title="Choose audio from library"
          >
            <i class="fa-sharp fa-light fa-folder-open" style="font-size:0.7rem;color:var(--color-text-tertiary);"></i>
          </button>
        `}
        <button class="btn btn-ghost btn-sm" style="min-height:28px;padding:2px 6px;"
          @click=${() => this._removePhrase(index)}
          title="Remove phrase"
        >
          <i class="fa-sharp fa-light fa-trash" style="font-size:0.7rem;color:var(--color-text-tertiary);"></i>
        </button>
      </div>
    `;
  }

  _confirmAddPhrase() {
    const text = this._newPhraseText?.trim();
    if (!text) return;
    this._updateRealism(r => {
      r.interruption_effects.phrases.push({ text, audioId: null });
    });
    this._addingPhrase = false;
    this._newPhraseText = '';
  }

  _removePhrase(index) {
    this._updateRealism(r => {
      r.interruption_effects.phrases.splice(index, 1);
    });
  }

  _removePhraseAudio(index) {
    this._updateRealism(r => {
      r.interruption_effects.phrases[index].audioId = null;
    });
  }

  /* ─── Section 3: Emotion ─── */

  _renderEmotion(realism) {
    const isOpen = this._expanded.has('emotion');
    const cfg = realism.emotion;
    return html`
      <div class="realism-section">
        <div class="section-toggle" @click=${() => this._toggleSection('emotion')}>
          <div class="section-icon" style="background:rgba(168,85,247,0.1);color:#a855f7;">
            <i class="fa-sharp fa-light fa-masks-theater"></i>
          </div>
          <span class="section-title">Emotion</span>
          <div class="toggle ${cfg.enabled ? 'active' : ''}"
            @click=${(e) => { e.stopPropagation(); this._updateRealism(r => { r.emotion.enabled = !r.emotion.enabled; }); }}
          ><div class="toggle-slider"></div></div>
          <i class="fa-sharp fa-light fa-chevron-right chevron ${isOpen ? 'open' : ''}"></i>
        </div>
        ${isOpen ? html`
          <div class="section-body">
            <div class="section-desc">
              Modify voice tone based on conversation context. Happy tone for good news, deeper sympathy for sad topics.
            </div>
            <label class="form-label" style="margin-top:var(--space-3);">Intensity</label>
            <div class="slider-row">
              <span class="slider-label">Subtle</span>
              <input type="range" min="0" max="1" step="0.1"
                .value=${String(cfg.intensity)}
                @input=${(e) => { this._updateRealism(r => { r.emotion.intensity = parseFloat(e.target.value); }); }}
              />
              <span class="slider-label" style="text-align:right;">Dramatic</span>
              <span class="slider-value">${Number(cfg.intensity).toFixed(1)}</span>
            </div>
            <div class="info-note">
              <i class="fa-sharp fa-light fa-circle-info"></i>
              <span>Works best with Azure TTS (express-as styles). Other providers use pitch/rate adjustments.</span>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  /* ─── Section 4: Background Noise ─── */

  _renderBackgroundNoise(realism) {
    const isOpen = this._expanded.has('background');
    const cfg = realism.background_noise;
    const allFiles = this._getFilesFor('background_noise');

    return html`
      <div class="realism-section">
        <div class="section-toggle" @click=${() => this._toggleSection('background')}>
          <div class="section-icon" style="background:rgba(34,197,94,0.1);color:#22c55e;">
            <i class="fa-sharp fa-light fa-volume-low"></i>
          </div>
          <span class="section-title">Background Noise</span>
          <div class="toggle ${cfg.enabled ? 'active' : ''}"
            @click=${(e) => { e.stopPropagation(); this._updateRealism(r => { r.background_noise.enabled = !r.background_noise.enabled; }); }}
          ><div class="toggle-slider"></div></div>
          <i class="fa-sharp fa-light fa-chevron-right chevron ${isOpen ? 'open' : ''}"></i>
        </div>
        ${isOpen ? html`
          <div class="section-body">
            <div class="section-desc">
              Ambient audio that loops during the call. Select one or more files — a random one will be picked per call.
            </div>

            <label class="form-label" style="margin-top:var(--space-3);">Volume</label>
            <div class="slider-row">
              <span class="slider-label">Quiet</span>
              <input type="range" min="0.05" max="0.8" step="0.05"
                .value=${String(cfg.volume)}
                @input=${(e) => {
                  const vol = parseFloat(e.target.value);
                  this._updateRealism(r => { r.background_noise.volume = vol; });
                  if (this._audioEl && this._playingId != null) this._audioEl.volume = vol;
                }}
              />
              <span class="slider-label" style="text-align:right;">Loud</span>
              <span class="slider-value">${Math.round(cfg.volume * 100)}%</span>
            </div>

            ${allFiles.length > 0 ? html`
              <div class="section-label">Audio Files</div>
              ${allFiles.map(f => this._renderSelectableAudio(f, 'background_noise', null, cfg.volume))}
            ` : html`
              <div style="font-size:var(--text-xs);color:var(--color-text-tertiary);padding:var(--space-3) 0;">
                No background noise files yet. Choose from the library below.
              </div>
            `}

            <button class="btn btn-secondary btn-sm" style="margin-top:var(--space-3);width:100%;"
              @click=${() => this._openLibrary('background_noise', null)}
            >
              <i class="fa-sharp fa-light fa-folder-open" style="margin-right:var(--space-1);"></i> Choose from Library
            </button>

            <div class="info-note">
              <i class="fa-sharp fa-light fa-circle-info"></i>
              <span>Audio files are shared across all agents. Add files from the library to use them.</span>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  /* ─── Section 5: Ask Coworker ─── */

  _renderAskCoworker(realism) {
    const isOpen = this._expanded.has('coworker');
    const cfg = realism.ask_coworker;
    const freqLabel = cfg.frequency <= 0.1 ? 'Rarely' : cfg.frequency <= 0.3 ? 'Sometimes' : 'Often';

    return html`
      <div class="realism-section">
        <div class="section-toggle" @click=${() => this._toggleSection('coworker')}>
          <div class="section-icon" style="background:rgba(59,130,246,0.1);color:#3b82f6;">
            <i class="fa-sharp fa-light fa-people-arrows"></i>
          </div>
          <span class="section-title">Coworker Side Conversation</span>
          <div class="toggle ${cfg.enabled ? 'active' : ''}"
            @click=${(e) => { e.stopPropagation(); this._updateRealism(r => { r.ask_coworker.enabled = !r.ask_coworker.enabled; }); }}
          ><div class="toggle-slider"></div></div>
          <i class="fa-sharp fa-light fa-chevron-right chevron ${isOpen ? 'open' : ''}"></i>
        </div>
        ${isOpen ? html`
          <div class="section-body">
            <div class="section-desc">
              The AI will naturally turn to talk to a nearby coworker without announcing it to the caller — as if the caller is overhearing a brief office side conversation.
            </div>

            <div class="form-group" style="margin-top:var(--space-3);">
              <label class="form-label">Coworker Name</label>
              <input class="form-input" type="text" placeholder="e.g. Christy"
                .value=${cfg.coworker_name}
                @change=${(e) => { this._updateRealism(r => { r.ask_coworker.coworker_name = e.target.value.trim(); }); }}
                style="max-width:250px;"
              />
            </div>

            <label class="form-label" style="margin-top:var(--space-3);">Frequency</label>
            <div class="slider-row">
              <span class="slider-label">Rarely</span>
              <input type="range" min="0.05" max="0.5" step="0.05"
                .value=${String(cfg.frequency)}
                @input=${(e) => { this._updateRealism(r => { r.ask_coworker.frequency = parseFloat(e.target.value); }); }}
              />
              <span class="slider-label" style="text-align:right;">Often</span>
              <span class="slider-value">${freqLabel}</span>
            </div>

            <div class="info-note">
              <i class="fa-sharp fa-light fa-circle-info"></i>
              <span>The AI won't tell the caller it's asking for help. It naturally talks to the coworker as if they're nearby — the caller just overhears a brief side conversation. Questions are contextual and never repeat.</span>
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  /* ─── Shared: Selectable Audio Card ─── */

  _renderSelectableAudio(file, category, itemType, volume) {
    return html`
      <div class="audio-card">
        <button class="btn btn-ghost btn-sm" style="min-height:24px;padding:2px 6px;"
          @click=${() => this._togglePlay(file, volume)}
        >
          <i class="fa-sharp fa-light ${this._playingId === file.id ? 'fa-stop' : 'fa-play'}" style="font-size:0.65rem;"></i>
        </button>
        <span class="file-name">${file.originalName}</span>
        <span class="file-size">${this._formatSize(file.fileSize)}</span>
        <button class="btn btn-ghost btn-sm" style="min-height:24px;padding:2px 6px;"
          @click=${() => this._unlinkAudioFile(file.id, category, itemType)}
          title="Remove from this section"
        >
          <i class="fa-sharp fa-light fa-xmark" style="font-size:0.65rem;color:var(--color-text-tertiary);"></i>
        </button>
      </div>
    `;
  }
}

customElements.define('agent-realism-tab', AgentRealismTab);
